/**
 * Catalogo: o que o plano de controle administra.
 *
 * Um conjunto so de funcoes para as quatro entidades simples, porque as rotas ja seguem o mesmo
 * formato: `/catalog/{entidade}` para listar, `/{id}` para alterar e excluir, `/{id}/resync` nos
 * espelhos do Auth. Repetir isso quatro vezes so daria quatro lugares para divergir.
 *
 * O SEGMENTO da URL nem sempre e o nome da entidade em ingles (`powerbi-connections`, com hifen) -
 * dai o tipo `Entidade` abaixo, que so aceita os segmentos que o backend realmente registra
 * (`_DEPENDENCY_ENTITIES` em `backend/app/api/routes/catalog.py`).
 */

import { apiDelete, apiGet, apiPost, apiPut, type Envelope } from './client'
import type {
  AccessPolicy,
  CatalogSummary,
  Client,
  Dataset,
  Dependencias,
  RotaDoAuth,
  Hospital,
  PowerBiConnection,
  Report,
} from './types'

/** Segmento da URL em `/catalog/{entidade}`. */
export type Entidade =
  | 'clients'
  | 'hospitals'
  | 'powerbi-connections'
  | 'datasets'
  | 'reports'
  | 'access-policies'

/** Resposta DIRETA, sem envelope: o `/summary` devolve o objeto, ao contrario das listagens. */
export function fetchSummary(): Promise<CatalogSummary> {
  return apiGet<CatalogSummary>('/catalog/summary')
}

export async function fetchManagerPaths(): Promise<RotaDoAuth[]> {
  return (await apiGet<Envelope<RotaDoAuth>>('/auth-catalog/manager-paths')).rows
}

export async function fetchReaderPaths(): Promise<RotaDoAuth[]> {
  return (await apiGet<Envelope<RotaDoAuth>>('/auth-catalog/reader-paths')).rows
}

export async function fetchClients(): Promise<Client[]> {
  return (await apiGet<Envelope<Client>>('/catalog/clients')).rows
}

export async function fetchHospitals(): Promise<Hospital[]> {
  return (await apiGet<Envelope<Hospital>>('/catalog/hospitals')).rows
}

export async function fetchConnections(): Promise<PowerBiConnection[]> {
  return (await apiGet<Envelope<PowerBiConnection>>('/catalog/powerbi-connections')).rows
}

export async function fetchDatasets(): Promise<Dataset[]> {
  return (await apiGet<Envelope<Dataset>>('/catalog/datasets')).rows
}

export async function fetchReports(): Promise<Report[]> {
  return (await apiGet<Envelope<Report>>('/catalog/reports')).rows
}

/**
 * So as policies de RLS. O backend admite `policy_type` como campo, mas hoje `rls` e o unico valor
 * aceito na criacao (`mvp_requires_rls_policy`); filtrar aqui evita que uma linha de outro tipo
 * apareca numa tela que so sabe falar de RLS.
 */
export async function fetchPolicies(): Promise<AccessPolicy[]> {
  const todas = (await apiGet<Envelope<AccessPolicy>>('/catalog/access-policies')).rows
  return todas.filter((item) => item.policy_type === 'rls')
}

/**
 * O que impede excluir. A regra continua sendo do BACKEND - isto so permite avisar antes de
 * tentar. Se esta consulta falhar, ainda da para tentar excluir: quem recusa e o servidor.
 */
export async function fetchDependencias(
  entidade: Entidade,
  id: string,
): Promise<Dependencias | undefined> {
  return (await apiGet<Envelope<Dependencias>>(`/catalog/${entidade}/${id}/dependencies`)).rows[0]
}

/** Repuxa nome, slug e status do Auth. So existe nos espelhos (clientes e hospitais). */
export function ressincronizar(entidade: Entidade, id: string): Promise<unknown> {
  return apiPost(`/catalog/${entidade}/${id}/resync`, {})
}

export function atualizar(
  entidade: Entidade,
  id: string,
  mudancas: Record<string, unknown>,
): Promise<unknown> {
  return apiPut(`/catalog/${entidade}/${id}`, mudancas)
}

export function excluir(entidade: Entidade, id: string): Promise<unknown> {
  return apiDelete(`/catalog/${entidade}/${id}`)
}
